// Field sources for transaction sub-resources — email_sends, invoice_links,
// transaction_transports. One presentation per sub-resource (mirrors
// `transactionFilePresentation`).
//
// definePresentation auto-includes every scalar column from COLUMN_META;
// relation embeds are declared with the canonical relation specs (alias
// preserved to match the app-layer embed field, e.g. `Transport.company`).
//
// These live in their own leaf module (not in transactionConfig.tsx) so both
// the config's sub-query defs AND the Faktury section can import them WITHOUT
// an import cycle — reading a foreign `*Presentation` at module-eval under a
// cycle TDZ-crashes at runtime (see memory/feedback_presentation_tdz_cycles).

import { definePresentation } from "@ds/config/presentation";
import { asDataSchema } from "@ds/config/presentationRefs";
import type { SchemaMeta } from "@ds/config/schema";
import type { ResolvedLinkWithInvoice } from "@shared/types/invoice";
import { db } from "@ds/config/db/graph";
import { companyChip, companyParty } from "@ds/config/db/refs";

export const emailSendPresentation = definePresentation("email_sends");

export const transportPresentation = definePresentation("transaction_transports", {
  // Embed alias is intentionally `company` (not the natural `transport_company`
  // derived from the FK). Pass it through the relation spec so BOTH the
  // emitted PostgREST embed AND the `data: (record) => record[alias]` closure
  // align — overriding `__alias` after the fact only updates the embed name,
  // not the data lookup, which is why the carriers / drivers / vehicles
  // cells were showing empty when the parent row HAD transports embedded.
  //
  // companyParty (not companyChip) — the transport company needs geocoded
  // `latitude`/`longitude` for map views, and companyChip doesn't carry them.
  // It renders the same CompanyChip component with the same `data` closure,
  // so this only widens the embed's column list, not the cell.
  company: companyParty.on(db.transaction_transports.transport_company_id, {
    alias: "company",
  }),
  // Billing company for the leg (payer) — distinct from the physical carrier
  // above. A DB trigger defaults it to the carrier when NULL, so it's always
  // set; it only diverges when a spedition bills. Natural alias already
  // matches the `Transport.commercial_company` join name, so no override.
  commercial_company: companyChip.on(db.transaction_transports.commercial_company_id),
});

// Invoice links are fetched from the `invoice_resolved_links` VIEW, not the
// base table: transport-leg links carry `transaction_id = NULL` on
// `invoice_links` (CHECK constraint — exactly one of transaction_id /
// transaction_transport_id), so only the view's resolved `transaction_id`
// (direct, or via the leg's parent transaction) reaches every link of a
// transaction. `entity_type`/`entity_id` say what the link points at.
export const transactionResolvedLinkPresentation = definePresentation("invoice_resolved_links", {
  // COLUMN_META only covers real tables, so a view's scalar columns must be
  // declared explicitly — nothing is auto-included here.
  entity_type: { column: db.invoice_resolved_links.entity_type },
  entity_id:   { column: db.invoice_resolved_links.entity_id },
  side:        { column: db.invoice_resolved_links.side },
  invoice: {
    __alias: "invoice",
    relation: {
      fk: db.invoice_resolved_links.invoice_id,
      targetCols: [
        db.invoices.invoice_number,
        db.invoices.direction,
        db.invoices.gross_amount,
        db.invoices.currency,
        db.invoices.review_status,
      ],
    },
  },
});

const RESOLVED_LINK_META: SchemaMeta = {
  resource:   "invoice_resolved_links",
  entityType: "invoice_link",
  softDelete: false,
  labels: {
    singular: (t) => t("invoices.links.singular", "Powiązanie"),
    plural:   (t) => t("invoices.links.plural",   "Powiązania"),
  },
};

/** Data schema for `kind:"data"` sections fetching a transaction's invoice links. */
export const transactionResolvedLinkDataSchema = asDataSchema<ResolvedLinkWithInvoice>(
  transactionResolvedLinkPresentation,
  RESOLVED_LINK_META,
);
